import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Users, Trophy, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PvpLobby() {
  const navigate = useNavigate();
  const [searching, setSearching] = useState(false);
  const [queueId, setQueueId] = useState(null);

  useEffect(() => {
    let interval;
    if (searching) {
      interval = setInterval(checkQueue, 2000);
    }
    return () => clearInterval(interval);
  }, [searching]);

  const joinQueue = async () => {
    try {
      setSearching(true);
      const response = await fetch(`${BACKEND_URL}/api/pvp/queue/join`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.status === "matched") {
        toast.success("Суперник знайдений!");
        navigate(`/pvp/battle/${data.match_id}`);
      } else {
        setQueueId(data.queue_id);
        toast.info("Пошук суперника...");
      }
    } catch (error) {
      toast.error("Помилка підключення");
      setSearching(false);
    }
  };

  const checkQueue = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pvp/queue/status`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.status === "matched") {
        setSearching(false);
        toast.success("Суперник знайдений!");
        navigate(`/pvp/battle/${data.match_id}`);
      }
    } catch (error) {
      console.error("Queue check error:", error);
    }
  };

  const cancelQueue = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/pvp/queue/leave`, {
        method: 'POST',
        credentials: 'include'
      });
      setSearching(false);
      setQueueId(null);
      toast.info("Пошук скасовано");
    } catch (error) {
      toast.error("Помилка скасування");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="grid-background min-h-screen flex flex-col">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Назад</span>
              </Button>
              <h1 className="text-xl font-bold text-foreground">Architecture Battle</h1>
              <div className="w-24"></div>
            </div>
          </div>
        </nav>

        <div className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-500 mb-6">
                <Swords className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-foreground mb-4">
                Architecture Battle
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Змагайся з іншими архітекторами в реальному часі. Проектуй систему та довів, що твоє рішення краще!
              </p>
            </div>

            {!searching ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    {
                      icon: Users,
                      title: "Знайди Суперника",
                      desc: "Система підбере гравця твого рівня"
                    },
                    {
                      icon: Swords,
                      title: "Проектуй Систему",
                      desc: "5 хвилин на вибір архітектурного рішення"
                    },
                    {
                      icon: Trophy,
                      title: "AI Суддя",
                      desc: "GPT-5.2 визначить переможця"
                    }
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl text-center"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-7 h-7 text-orange-500" />
                      </div>
                      <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="text-center">
                  <Button
                    onClick={joinQueue}
                    data-testid="start-pvp-button"
                    size="lg"
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white h-16 px-12 text-xl rounded-xl font-bold transition-all duration-200 shadow-[0_0_30px_rgba(249,115,22,0.4)] hover:shadow-[0_0_40px_rgba(249,115,22,0.6)]"
                  >
                    <Swords className="w-6 h-6 mr-3" />
                    Почати Battle
                  </Button>
                </div>

                <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-2xl">
                  <h3 className="text-2xl font-bold text-foreground mb-6 text-center">Як це працює?</h3>
                  <div className="space-y-4 max-w-2xl mx-auto">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <span className="text-orange-500 font-bold">1</span>
                      </div>
                      <p className="text-muted-foreground">Отримайте архітектурний кейс (наприклад: Ride-sharing система)</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <span className="text-orange-500 font-bold">2</span>
                      </div>
                      <p className="text-muted-foreground">Оберіть компоненти: архітектурний стиль, базу даних, кешування, messaging, масштабування</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <span className="text-orange-500 font-bold">3</span>
                      </div>
                      <p className="text-muted-foreground">AI суддя аналізує обидва рішення за критеріями: масштабованість, продуктивність, вартість</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <span className="text-orange-500 font-bold">4</span>
                      </div>
                      <p className="text-muted-foreground">Переможець отримує ELO рейтинг та піднімається в таблиці лідерів!</p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/pvp/leaderboard')}
                    data-testid="pvp-leaderboard-button"
                    className="h-12 px-8"
                  >
                    <Trophy className="w-5 h-5 mr-2" />
                    Таблиця PvP Лідерів
                  </Button>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-card/50 backdrop-blur-sm border border-orange-500/30 p-12 rounded-2xl text-center max-w-2xl mx-auto"
              >
                <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-6" />
                <h3 className="text-3xl font-bold text-foreground mb-4">Пошук суперника...</h3>
                <p className="text-lg text-muted-foreground mb-8">
                  Шукаємо архітектора твого рівня для епічної битви!
                </p>
                <Button
                  variant="outline"
                  onClick={cancelQueue}
                  data-testid="cancel-queue-button"
                  className="h-12 px-8 border-destructive text-destructive hover:bg-destructive/10"
                >
                  Скасувати пошук
                </Button>
              </motion.div>
            )}
          </motion.div>
        </div>
        
        <Footer />
      </div>
    </div>
  );
}
